import EngineShell, { type SubTabDef } from '../components/EngineShell';
import DataIntervention, {
  ActionIntervention, MetricTiles, MiniTable, StatusPill,
} from '../components/engine/DataIntervention';
import { api } from '../api/client';
import { useState } from 'react';

/**
 * Engine 4 · Distribution (CPG value chain · stage 4, route-to-market).
 * The reach + cost-to-serve lever. Route Optimization is the lighthouse.
 */

interface RoutePlan { route_id: string; name: string; drops: number; miles: number; cases: number; sequence: number }
interface RouteResp { plan: RoutePlan[]; miles_per_drop: number; drop_size: number; route_efficiency: number }

interface ChannelCost { channel: string; cost_per_unit: number; gross_margin_pct: number }
interface BodegaEconomics { avg_drop_cases: number; cash_margin_per_drop_usd: number; service_note: string }
interface CostToServeResp { cost_per_unit_by_channel: ChannelCost[]; bodega_economics: BodegaEconomics }

interface DensityLane { lane: string; cases_per_pallet: number; miles: number; density_state: string }
interface FreightDensityResp { pallet_density: number; proximity_score: number; lanes: DensityLane[] }

interface ReplenishAlert { retailer: string; sku: string; dc: string; severity: string; action: string }
interface OosResp { oos_rate: number; fill_rate: number; replenish_alerts: ReplenishAlert[] }

interface TouchLane { lane: string; touches: number; target: number }
interface ReductionTarget { lane: string; target_reduction: number; action: string }
interface HandlingResp { touch_count_by_lane: TouchLane[]; reduction_targets: ReductionTarget[] }

interface WarehouseResp { pallets_used: number; capacity: number; asrs_status: string }

const pct = (v: number) => `${v.toFixed(1)}%`;

export default function Engine4Shell() {
  const subTabs: SubTabDef[] = [
    {
      id: 'route_optimize', name: 'Route Optimization', tier: 'lighthouse', moat: 'core', enabled: true,
      render: () => <RouteOptimize />,
    },
    {
      id: 'cost_to_serve', name: 'Cost-to-Serve', tier: 'flagship', moat: 'core', enabled: true,
      render: () => (
        <DataIntervention<CostToServeResp>
          objective="O4 · keep every channel economic, defend the bodega 99c single"
          workflow={['Pull cost-per-unit by channel', 'Compare gross margins', 'Protect high-density drops', 'Re-mix the unprofitable channels']}
          decisionRules={{ rules: ['Flag channel with gross margin < 30%', 'Protect the bodega single-can economics'] }}
          aiLogic={{ description: 'Deterministic cost-to-serve rollup by channel.', modality: 'deterministic', hitlLevel: 'L2 exception review', moat: 'core' }}
          kpi={{ primary: 'Cost-per-unit + gross margin by channel', owner: 'VP Sales / Logistics' }}
          queryKey={['distribution', 'cost-to-serve']} fetcher={() => api.get<CostToServeResp>('/api/distribution/cost-to-serve')}
          render={(d) => (
            <>
              <MiniTable title="Cost per unit by channel" rows={d.cost_per_unit_by_channel} rowKey={(r) => r.channel}
                columns={[
                  { header: 'Channel', cell: (r) => <b>{r.channel}</b> },
                  { header: 'Cost/unit', align: 'right', cell: (r) => <span className="mono">${r.cost_per_unit.toFixed(3)}</span> },
                  { header: 'Gross margin', align: 'right', cell: (r) => <span className="mono" style={{ color: r.gross_margin_pct >= 30 ? 'var(--olive)' : 'var(--gold)' }}>{pct(r.gross_margin_pct)}</span> },
                ]} />
              <div className="panel" style={{ background: 'var(--panel2)' }}>
                <div className="small faint" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Bodega economics</div>
                <div className="small">Avg drop: <b className="mono">{d.bodega_economics.avg_drop_cases} cases</b> · cash margin/drop: <b className="mono" style={{ color: 'var(--olive)' }}>${d.bodega_economics.cash_margin_per_drop_usd.toFixed(0)}</b></div>
                <div className="small faint" style={{ marginTop: 4 }}>{d.bodega_economics.service_note}</div>
              </div>
            </>
          )}
        />
      ),
    },
    {
      id: 'freight_density', name: 'Freight Density', tier: 'flagship', moat: 'core', enabled: true,
      render: () => (
        <DataIntervention<FreightDensityResp>
          objective="O4 · pack pallets dense, ship short lanes"
          workflow={['Measure pallet density + proximity', 'Score density by lane', 'Re-build light pallets', 'Shorten long lanes']}
          decisionRules={{ rules: ['Amber if lane density below plan', 'Prioritize short-proximity lanes'] }}
          aiLogic={{ description: 'Deterministic pallet-density + proximity scoring per lane.', modality: 'deterministic', hitlLevel: 'L2 exception review', moat: 'core' }}
          kpi={{ primary: 'Pallet density + proximity by lane', owner: 'Logistics' }}
          queryKey={['distribution', 'freight-density']} fetcher={() => api.get<FreightDensityResp>('/api/distribution/freight-density')}
          render={(d) => (
            <>
              <MetricTiles tiles={[
                { label: 'Pallet density', value: pct(d.pallet_density * 100), color: d.pallet_density >= 0.85 ? 'olive' : 'gold' },
                { label: 'Proximity score', value: pct(d.proximity_score * 100), color: 'ink' },
              ]} />
              <MiniTable title="By lane" rows={d.lanes} rowKey={(r, i) => `${r.lane}-${i}`}
                columns={[
                  { header: 'Lane', cell: (r) => <span className="small">{r.lane}</span> },
                  { header: 'Cases/pallet', align: 'right', cell: (r) => <span className="mono">{r.cases_per_pallet}</span> },
                  { header: 'Miles', align: 'right', cell: (r) => <span className="mono">{r.miles}</span> },
                  { header: 'Density', align: 'right', cell: (r) => <StatusPill status={r.density_state} /> },
                ]} />
            </>
          )}
        />
      ),
    },
    {
      id: 'oos_fill_rate', name: 'OOS / Fill Rate', tier: 'flagship', moat: 'core', enabled: true,
      render: () => (
        <DataIntervention<OosResp>
          objective="O4 · keep shelves full — cut out-of-stocks, lift fill rate"
          workflow={['Pull OOS + fill rate', 'Rank replenishment alerts', 'Expedite high-velocity gaps', 'Confirm delivery wave']}
          decisionRules={{ rules: ['Expedite if OOS risk is amber/red', 'Protect hero-can availability'] }}
          aiLogic={{ description: 'Deterministic OOS/fill computation with replenishment alerting.', modality: 'deterministic', hitlLevel: 'L2 exception review', moat: 'core' }}
          kpi={{ primary: 'OOS rate + fill rate + open alerts', owner: 'VP Sales / Replenishment' }}
          queryKey={['distribution', 'oos']} fetcher={() => api.get<OosResp>('/api/distribution/oos')}
          render={(d) => (
            <>
              <MetricTiles tiles={[
                { label: 'OOS rate', value: pct(d.oos_rate * 100), color: d.oos_rate <= 0.05 ? 'olive' : 'gold' },
                { label: 'Fill rate', value: pct(d.fill_rate * 100), color: d.fill_rate >= 0.95 ? 'olive' : 'gold' },
              ]} />
              <MiniTable title="Replenishment alerts" rows={d.replenish_alerts} rowKey={(r, i) => `${r.retailer}-${i}`}
                columns={[
                  { header: 'Retailer · DC', cell: (r) => <><b>{r.retailer}</b> <span className="small faint">{r.dc}</span></> },
                  { header: 'SKU', cell: (r) => <span className="small">{r.sku}</span> },
                  { header: 'Severity', align: 'right', cell: (r) => <StatusPill status={r.severity} /> },
                ]} />
              {d.replenish_alerts[0] && <div className="small faint">Next action: {d.replenish_alerts[0].action}</div>}
            </>
          )}
        />
      ),
    },
    {
      id: 'handling_touches', name: 'Handling Touches', tier: 'flagship', moat: 'commodity', enabled: true,
      render: () => (
        <DataIntervention<HandlingResp>
          objective="O4 · cut handling touches per lane (hygiene)"
          workflow={['Count touches vs target by lane', 'Identify reduction targets', 'Crossdock / pre-build to cut touches']}
          decisionRules={{ rules: ['Flag any lane above touch target', 'Standardize lane handling'] }}
          aiLogic={{ description: 'Deterministic touch-count rollup vs targets.', modality: 'deterministic', hitlLevel: 'L1 sampling', moat: 'commodity' }}
          kpi={{ primary: 'Touches vs target by lane', owner: 'Logistics' }}
          queryKey={['distribution', 'handling']} fetcher={() => api.get<HandlingResp>('/api/distribution/handling-touches')}
          render={(d) => (
            <>
              <MiniTable title="Touches by lane" rows={d.touch_count_by_lane} rowKey={(r, i) => `${r.lane}-${i}`}
                columns={[
                  { header: 'Lane', cell: (r) => <span className="small">{r.lane}</span> },
                  { header: 'Touches', align: 'right', cell: (r) => <span className="mono" style={{ color: r.touches > r.target ? 'var(--gold)' : 'var(--olive)' }}>{r.touches}</span> },
                  { header: 'Target', align: 'right', cell: (r) => <span className="mono faint">{r.target}</span> },
                ]} />
              <MiniTable title="Reduction targets" rows={d.reduction_targets} rowKey={(r, i) => `${r.lane}-${i}`}
                columns={[
                  { header: 'Lane', cell: (r) => <span className="small">{r.lane}</span> },
                  { header: 'Reduction', align: 'right', cell: (r) => <span className="mono">−{r.target_reduction}</span> },
                  { header: 'Action', cell: (r) => <span className="small faint">{r.action}</span> },
                ]} />
            </>
          )}
        />
      ),
    },
    {
      id: 'warehouse', name: 'Warehouse', tier: 'flagship', moat: 'commodity', enabled: true,
      render: () => (
        <DataIntervention<WarehouseResp>
          objective="O4 · keep warehouse utilization healthy (hygiene)"
          workflow={['Pull pallets used vs capacity', 'Compute utilization', 'Watch ASRS status']}
          decisionRules={{ rules: ['Amber if utilization > 85%', 'Escalate ASRS faults'] }}
          aiLogic={{ description: 'Deterministic warehouse utilization from pallet state.', modality: 'deterministic', hitlLevel: 'L1 sampling', moat: 'commodity' }}
          kpi={{ primary: 'Pallets used / capacity + ASRS status', owner: 'Warehouse ops' }}
          queryKey={['distribution', 'warehouse']} fetcher={() => api.get<WarehouseResp>('/api/distribution/warehouse')}
          render={(d) => {
            const util = d.capacity ? (d.pallets_used / d.capacity) * 100 : 0;
            return (
              <MetricTiles tiles={[
                { label: 'Pallets', value: `${d.pallets_used.toLocaleString()} / ${d.capacity.toLocaleString()}`, color: 'ink' },
                { label: 'Utilization', value: pct(util), color: util <= 85 ? 'olive' : 'gold' },
                { label: 'ASRS', value: d.asrs_status, color: d.asrs_status === 'red' ? 'red' : d.asrs_status === 'amber' ? 'gold' : 'olive' },
              ]} />
            );
          }}
        />
      ),
    },
  ];

  return (
    <EngineShell
      engineId={4}
      engineName="Engine 4 · Distribution (Route-to-Market)"
      engineCode="E4"
      subTabs={subTabs}
      backgrounds={[{ id: 'carrier', name: 'Carrier Scan', status: 'ok' }]}
    />
  );
}

/* Route Optimization — POST with a window select */
function RouteOptimize() {
  const [window, setWindow] = useState('week');
  return (
    <ActionIntervention<RouteResp>
      objective="O4 · build dense, efficient DSD routes"
      workflow={['Pick a planning window', 'Optimize drop sequence', 'Score miles-per-drop + efficiency', 'Dispatch the plan']}
      decisionRules={{ rules: ['Maximize cases per mile', 'Hold drop density on bodega routes'] }}
      aiLogic={{ description: 'Deterministic route optimization over the drop network.', modality: 'deterministic', hitlLevel: 'L2 exception review', moat: 'core' }}
      kpi={{ primary: 'Miles-per-drop + drop size + route efficiency', owner: 'Logistics / DSD' }}
      initialState={{
        miles_per_drop: 8.7,
        drop_size: 42,
        route_efficiency: 0.91,
        plan: [{ route_id: 'NYC-Bodega-12', name: 'NYC bodega loop', drops: 18, miles: 164, cases: 760, sequence: 1 }],
      }}
      input={
        <>
          <div className="small faint" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Window</div>
          <select className="c2-input" value={window} onChange={(e) => setWindow(e.target.value)} style={{ width: 160 }}>
            <option value="day">Day</option>
            <option value="week">Week</option>
            <option value="month">Month</option>
          </select>
        </>
      }
      runLabel="Optimize routes"
      action={() => api.distributionRouteOptimize(window) as Promise<RouteResp>}
      render={(d) => (
        <>
          <MetricTiles tiles={[
            { label: 'Miles / drop', value: d.miles_per_drop.toFixed(2), color: 'ink' },
            { label: 'Drop size', value: `${d.drop_size.toFixed(1)} cases`, color: 'ink' },
            { label: 'Route efficiency', value: pct(d.route_efficiency * 100), color: 'olive' },
          ]} />
          <MiniTable title="Route plan" rows={d.plan} rowKey={(r) => r.route_id}
            columns={[
              { header: 'Seq', cell: (r) => <b className="mono">{r.sequence}</b> },
              { header: 'Route', cell: (r) => <><b className="mono">{r.route_id}</b> · {r.name}</> },
              { header: 'Drops', align: 'right', cell: (r) => <span className="mono">{r.drops}</span> },
              { header: 'Miles', align: 'right', cell: (r) => <span className="mono">{r.miles.toFixed(0)}</span> },
              { header: 'Cases', align: 'right', cell: (r) => <span className="mono">{r.cases.toLocaleString()}</span> },
            ]} />
        </>
      )}
    />
  );
}
